import { ChronoUnit } from "@js-joda/core";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { TIME_UNITS } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import {
  defaultDatasetTableConfig,
  totalTypeDropdownSelectorOptions,
} from "components/dashboard/dataset-table/dataset-table-editor/defaultDatasetTableEditorConfigs";
import GroupedHeadersDatasetSelector from "components/dashboard/dataset-table/GroupedHeadersDatasetSelector";
import { DatasetTableMode } from "components/dashboard/dataset-table/utils/interfaces";
import { SortDirection } from "gql/generated/types";
import {
  AdditionalDateColumnType,
  ComponentConfigDefault,
  DatasetFullDefaultConfig,
  DayOfWeekDisplayType,
} from "models/dataset-models";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { textAligns } from "utils/select-items";

const DatasetTableDatasetEditor = lazy(
  () =>
    import(
      "components/dashboard/dataset-table/dataset-table-editor/dataset-table-dataset-editor/DatasetTableDatasetEditor"
    )
);

const sortOptions = [
  { label: T("word.ascending"), value: SortDirection.Asc },
  { label: T("word.descending"), value: SortDirection.Desc },
];

const additionalDateColumnOptions = [
  { label: T("word.none"), value: AdditionalDateColumnType.NONE },
  { label: T("word.week"), value: AdditionalDateColumnType.WEEK },
  { label: T("word.day-of-week"), value: AdditionalDateColumnType.DAY_OF_WEEK },
];

const dayOfWeekDisplayTypeOptions = [
  { label: T("word.short"), value: DayOfWeekDisplayType.SHORT },
  { label: T("word.long"), value: DayOfWeekDisplayType.LONG },
];

const DatasetTableEditor: React.FC<DashboardEditorProps<DatasetFullDefaultConfig>> = ({
  config,
  onConfigChange,
}) => {
  let conf = config ?? defaultDatasetTableConfig;

  if (Object.keys(conf).length === 0) {
    conf = defaultDatasetTableConfig;
  }

  const [currentConfig, setCurrentConfig] = useState(conf);
  const [datasetEditorIsVisible, setDatasetEditorVisibility] = useState(false);
  const isSingleMode = currentConfig.mode === DatasetTableMode.SINGLE || currentConfig.mode === undefined;

  const selectedDatasets = currentConfig.datasets?.map(d => d.identifier) ?? [];

  const setConfigPropertyState = useCallback(
    (property: keyof ComponentConfigDefault, value: any) => {
      const newConfig = structuredClone(currentConfig);
      if (newConfig.config) {
        newConfig.config[property] = value;
      }

      setCurrentConfig(newConfig);
    },
    [currentConfig]
  );

  const handleForceLtTz = useCallback(
    (_: any, value: boolean) => {
      const newConfig = structuredClone(currentConfig);
      newConfig.legacyForcedLtTimezone = value;
      onConfigChange(newConfig);
      setCurrentConfig(newConfig);
    },
    [currentConfig, onConfigChange]
  );

  const handleConfigChange = useCallback(
    (id: string, value: any): void => {
      setConfigPropertyState(id as keyof ComponentConfigDefault, value);
    },
    [setConfigPropertyState]
  );

  const handleModeChange = useCallback(
    (e: any): void => {
      const tempConfig = structuredClone(currentConfig);
      tempConfig.mode = e.value ? DatasetTableMode.GROUPED : DatasetTableMode.SINGLE;
      onConfigChange(tempConfig);
      setCurrentConfig(tempConfig);
    },
    [currentConfig, onConfigChange]
  );

  const handleTimeUnitsChange = useCallback(
    (e: any): void => {
      const tempConfig = structuredClone(currentConfig);

      tempConfig.config![e.target.id as keyof ComponentConfigDefault] = e.target.value
        .toString()
        .toUpperCase();
      onConfigChange(tempConfig);
      setCurrentConfig(tempConfig);
    },
    [currentConfig, onConfigChange]
  );

  const handleDatasetChange = useCallback(
    (e: string[]) => {
      const selectedDatasetsArray = e.map(identifier => {
        const oldDataset = currentConfig.datasets?.find(data => data.identifier === identifier);
        return oldDataset ?? { identifier };
      });

      setCurrentConfig({
        ...currentConfig,
        datasets: selectedDatasetsArray,
      });
    },
    [currentConfig]
  );

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  return (
    <div className="grid p-fluid">
      {isSingleMode ? (
        <div className="flex gap-2 w-full pl-2 pr-2">
          <DatasetMultiSelect value={selectedDatasets} onChange={handleDatasetChange} />
          <Button
            icon="pi pi-pencil"
            type="button"
            className="p-button-secondary"
            tooltip={T("action.edit-dataset-config")}
            onClick={() => setDatasetEditorVisibility(true)}
          />
          <Dialog
            onHide={() => setDatasetEditorVisibility(false)}
            visible={datasetEditorIsVisible}
            header={T("action.edit-dataset-config")}
            style={{ width: "min(100vw, 1200px)", height: "500px" }}
          >
            <Suspense fallback={<ProgressSpinner />}>
              <DatasetTableDatasetEditor
                currentConfig={currentConfig}
                setCurrentConfig={setCurrentConfig}
                onConfigChange={newConfig => setCurrentConfig(newConfig)}
              />
            </Suspense>
          </Dialog>
        </div>
      ) : (
        <div className="col-12 gap-2 flex flex-column justify-content-center align-items-center">
          <GroupedHeadersDatasetSelector currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
        </div>
      )}
      <div className="flex gap-2 w-full justify-content-between text-center mx-2 my-3">
        <div className="gap-2 flex flex-column justify-content-between align-items-center">
          <label htmlFor="mode">{T("component.column-headers")}</label>
          <InputSwitch
            id="mode"
            tooltip={currentConfig.mode === DatasetTableMode.SINGLE ? "Single" : "Grouped"}
            checked={currentConfig.mode === DatasetTableMode.GROUPED}
            onChange={e => handleModeChange(e)}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-between align-items-center">
          <label htmlFor="transposed">{T("component.transposed")}</label>
          <InputSwitch
            id="transposed"
            checked={Boolean(currentConfig.config?.transposed)}
            onChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-between align-items-center">
          <label htmlFor="hideDateColumn">{T("component.hide-date-column")}</label>
          <InputSwitch
            id="hideDateColumn"
            checked={Boolean(currentConfig.config?.hideDateColumn)}
            onChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-between align-items-center">
          <label htmlFor="showEmpty">{T("component.show-empty")}</label>
          <InputSwitch
            id="showEmpty"
            checked={Boolean(currentConfig.config?.showEmpty)}
            onChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-between align-items-center">
          <label htmlFor="showTotal">{T("component.show-total")}</label>
          <InputSwitch
            id="showTotal"
            checked={Boolean(currentConfig.config?.showTotal)}
            onChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-between align-items-center">
          <label htmlFor="hidePagination">{T("action.hide-pagination")}</label>
          <InputSwitch
            id="hidePagination"
            checked={Boolean(currentConfig.config?.hidePagination)}
            onChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-between align-items-center">
          <label htmlFor="legacyForcedLtTimezone">Force LT Timezone</label>
          <InputSwitch
            id="legacyForcedLtTimezone"
            checked={Boolean(currentConfig.legacyForcedLtTimezone ?? true)}
            onChange={e => handleForceLtTz(e.target.id, e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap md:flex-nowrap justify-content-between text-center ml-2 mb-3">
        <div className="flex-1 flex gap-2">
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="startOffset">{T("load-conf.start-offset")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="startOffset"
                className="w-6"
                value={currentConfig.config?.startOffset as number}
                onValueChange={e => handleConfigChange(e.target.id, e.target.value)}
              />
              <TimeUnitDropdown
                id="startOffsetUnits"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.config?.startOffsetUnits ? currentConfig.config.startOffsetUnits : ""}
                onChange={handleTimeUnitsChange}
              />
            </div>
          </div>
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="stepSize">{T("load-conf.step-size")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="stepSize"
                className="w-6"
                min={1}
                value={currentConfig.config?.stepSize as number}
                onValueChange={e => handleConfigChange(e.target.id, e.target.value)}
              />
              <TimeUnitDropdown
                id="stepUnits"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.config?.stepUnits ? currentConfig.config.stepUnits : ""}
                onChange={handleTimeUnitsChange}
              />
            </div>
          </div>
        </div>
        <div className="flex-1 flex gap-2">
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="lagOffset">{T("load-conf.lag-offset")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="lagOffset"
                className="w-6"
                value={currentConfig.config?.lagOffset as number}
                onValueChange={e => handleConfigChange(e.target.id, e.target.value)}
              />
              <TimeUnitDropdown
                id="lagUnits"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.config?.lagUnits ? currentConfig.config.lagUnits : ""}
                onChange={handleTimeUnitsChange}
              />
            </div>
          </div>
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="leadOffset">{T("load-conf.lead-offset")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="leadOffset"
                className="w-6"
                value={currentConfig.config?.leadOffset as number}
                onValueChange={e => handleConfigChange(e.target.id, e.target.value)}
              />
              <TimeUnitDropdown
                id="leadUnits"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.config?.leadUnits ? currentConfig.config.leadUnits : ""}
                onChange={handleTimeUnitsChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="w-full flex gap-2 flex-wrap md:flex-nowrap ml-2 mb-3">
        <div className="flex-1 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="totalType">{T("component.total-type")}</label>
          <Dropdown
            id="totalType"
            value={currentConfig.config?.totalType}
            options={totalTypeDropdownSelectorOptions}
            className="w-full"
            onChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
        </div>
        <div className="flex-1 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="alignStartTo">{T("load-conf.align-start-to")}</label>
          <TimeUnitDropdown
            id="alignStartTo"
            className="w-full p-0"
            value={currentConfig.config?.alignStartTo ? currentConfig.config.alignStartTo : ""}
            onChange={handleTimeUnitsChange}
          />
        </div>
        <div className="flex-1 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="startAutoRefreshFrequency">{T("load-conf.update-frequency")}</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="startAutoRefreshFrequency"
              value={currentConfig.config?.startAutoRefreshFrequency as number}
              onValueChange={e => handleConfigChange(e.target.id, e.target.value)}
            />
            <span className="p-inputgroup-addon">s</span>
          </div>
        </div>
        <div className="flex-1 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="dateFormat">{T("component.date-format")}</label>
          <TimeUnitDropdown
            id="dateFormat"
            className="w-full p-0"
            options={TIME_UNITS.filter(unit => unit.value !== ChronoUnit.WEEKS)}
            value={currentConfig.config?.dateFormat ? currentConfig.config.dateFormat : ""}
            onChange={handleTimeUnitsChange}
          />
        </div>
        <div className="flex-1 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="itemsPerPage">{T("component.items-per-page")}</label>
          <InputNumber
            id="itemsPerPage"
            value={currentConfig.config?.itemsPerPage}
            onValueChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
        </div>
      </div>
      <div className="w-full flex gap-2 flex-wrap md:flex-nowrap ml-2 mb-3">
        <div className="flex flex-column gap-2 w-2">
          <label htmlFor="sort">{T("load-conf.default-date-sorting")}</label>
          <Dropdown
            id="sort"
            value={currentConfig.config?.sort ?? SortDirection.Asc}
            options={sortOptions}
            onChange={(e: DropdownChangeEvent) => handleConfigChange(e.target.id, e.target.value as string)}
          />
        </div>
        <div className="gap-2 w-3 flex flex-column justify-content-center align-items-center">
          <label htmlFor="textAlign">{T("word.align")}</label>
          <Dropdown
            id="textAlign"
            value={currentConfig.config?.textAlign}
            className="w-full"
            options={textAligns}
            optionLabel="label"
            placeholder={T("label.custom")}
            showClear
            onChange={e => handleConfigChange(e.target.id, e.target.value)}
          />
        </div>
        <div className="flex flex-column gap-2 w-3">
          <label htmlFor="additionalDateColumn">{T("load-conf.additional-date-column")}</label>
          <Dropdown
            id="additionalDateColumn"
            value={currentConfig.config?.additionalDateColumn}
            options={additionalDateColumnOptions}
            onChange={(e: DropdownChangeEvent) => handleConfigChange(e.target.id, e.target.value as string)}
          />
        </div>
        {currentConfig.config?.additionalDateColumn &&
          currentConfig.config.additionalDateColumn === AdditionalDateColumnType.DAY_OF_WEEK && (
            <div className="flex flex-column gap-2 w-3">
              <label htmlFor="">{T("load-conf.day-of-week-display-type")}</label>
              <Dropdown
                id="dayOfWeekDisplayType"
                value={currentConfig.config?.dayOfWeekDisplayType ?? DayOfWeekDisplayType.SHORT}
                options={dayOfWeekDisplayTypeOptions}
                onChange={(e: DropdownChangeEvent) =>
                  handleConfigChange(e.target.id, e.target.value as string)
                }
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default DatasetTableEditor;
